/**
 * Moyenne qui nivelle — logique pure (Boîte à outils, 6e → 2nde).
 *
 * Moyenne = partage égal : ce qui dépasse la moyenne comble exactement ce
 * qui manque (Σ (xᵢ − m) = 0). Moyenne pondérée par des effectifs ou des
 * coefficients. Diagrammes en barres et circulaire depuis un tableau
 * d'effectifs : fréquences et angles (angle = fréquence × 360°).
 * Tout en fractions exactes ; une valeur qui ne tombe pas juste s'écrit en
 * fraction suivie de « ≈ » au centième.
 */

import { add, frac, mul, sub, type Frac } from '../../ui/exact';

export const fv = (f: Frac) => f.p / f.q;

const NNBSP = String.fromCharCode(0x202f);

/** p/q a-t-il une écriture décimale d'au plus `k` décimales ? */
export const exactWith = (f: Frac, k: number) => (f.p * 10 ** k) % f.q === 0;

/** arrondi de p/q à `k` décimales, en ENTIERS (3/40 → 0,08 ; à égale distance, au-dessus) */
export function roundText(f: Frac, k: number): string {
  const n = f.p * 10 ** k;
  const neg = n < 0;
  const r = Math.floor((2 * Math.abs(n) + f.q) / (2 * f.q));
  const s = String(r).padStart(k + 1, '0');
  const t = k ? `${s.slice(0, -k)},${s.slice(-k)}`.replace(/,?0+$/, '') : s;
  return `${neg && r ? '−' : ''}${t}`;
}

/**
 * Décimal exact s'il a au plus 3 décimales (81/8 = 10,125), sinon « p/q ≈ … »
 * au centième (ou seulement « ≈ … » si `approxOnly`).
 */
export function show(f: Frac, approxOnly = false): string {
  if (exactWith(f, 3)) return roundText(f, 3);
  const a = `≈ ${roundText(f, 2)}`;
  return approxOnly ? a : `${f.p < 0 ? '−' : ''}${Math.abs(f.p)}/${f.q} ${a}`;
}

export const sum = (xs: readonly number[]) => xs.reduce((s, x) => s + x, 0);

export function mean(xs: readonly number[]): Frac {
  return frac(sum(xs), xs.length);
}

/** « (12 + 7 + 15) ÷ 3 = 34 ÷ 3 = 34/3 ≈ 11,33 » */
export function meanText(xs: readonly number[]): string {
  const s = sum(xs);
  const m = mean(xs);
  return `(${xs.join(' + ')}) ÷ ${xs.length} = ${s} ÷ ${xs.length} = ${show(m)}`;
}

/** ce qui dépasse la moyenne, ce qui manque : toujours égaux */
export function excessDeficit(xs: readonly number[]): {
  above: Array<{ i: number; d: Frac }>;
  below: Array<{ i: number; d: Frac }>;
  excess: Frac;
  deficit: Frac;
} {
  const m = mean(xs);
  const above: Array<{ i: number; d: Frac }> = [];
  const below: Array<{ i: number; d: Frac }> = [];
  let excess = frac(0);
  let deficit = frac(0);
  xs.forEach((x, i) => {
    const d = sub(frac(x), m);
    if (d.p > 0) {
      above.push({ i, d });
      excess = add(excess, d);
    } else if (d.p < 0) {
      const e = frac(-d.p, d.q);
      below.push({ i, d: e });
      deficit = add(deficit, e);
    }
  });
  return { above, below, excess, deficit };
}

/* ---------- moyenne pondérée ---------- */

export interface Row {
  x: number;
  /** effectif ou coefficient */
  n: number;
}

export function weightedMean(rows: readonly Row[]): Frac {
  const tot = sum(rows.map((r) => r.n));
  return frac(sum(rows.map((r) => r.n * r.x)), tot);
}

/** « (2 × 12 + 1 × 8 + 3 × 15) ÷ (2 + 1 + 3) = 77 ÷ 6 = 77/6 ≈ 12,83 » */
export function weightedText(rows: readonly Row[]): string {
  const tot = sum(rows.map((r) => r.n));
  const s = sum(rows.map((r) => r.n * r.x));
  return `(${rows.map((r) => `${r.n} × ${r.x}`).join(' + ')}) ÷ (${rows.map((r) => r.n).join(' + ')}) = ${s} ÷ ${tot} = ${show(weightedMean(rows))}`;
}

/* ---------- diagrammes ---------- */

export interface Category {
  label: string;
  n: number;
}

export interface Dataset {
  id: string;
  title: string;
  cats: Category[];
}

export const DATASETS: readonly Dataset[] = [
  {
    id: 'transport',
    title: 'Comment les élèves viennent au collège',
    cats: [
      { label: 'Bus', n: 12 },
      { label: 'À pied', n: 8 },
      { label: 'Vélo', n: 4 },
      { label: 'Voiture', n: 6 },
    ],
  },
  {
    id: 'sport',
    title: 'Sport préféré',
    cats: [
      { label: 'Football', n: 9 },
      { label: 'Basket', n: 5 },
      { label: 'Natation', n: 6 },
      { label: 'Danse', n: 4 },
    ],
  },
  {
    id: 'pets',
    title: 'Animal de compagnie',
    cats: [
      { label: 'Chat', n: 10 },
      { label: 'Chien', n: 8 },
      { label: 'Poisson', n: 3 },
      { label: 'Aucun', n: 9 },
    ],
  },
];

export interface Share {
  freq: Frac;
  /** « 12/30 = 2/5 = 0,4 = 40 % » */
  freqText: string;
  /** angle en degrés, fraction exacte */
  angle: Frac;
  angleText: string;
}

export function shares(cats: readonly Category[]): Share[] {
  const N = sum(cats.map((c) => c.n));
  return cats.map((c) => {
    if (N === 0) return { freq: frac(0), freqText: '—', angle: frac(0), angleText: '—' };
    const f = frac(c.n, N);
    const pct = mul(f, frac(100));
    // forme réduite seulement si elle apporte quelque chose (pas « 12/12 = 1/1 »)
    const reduced = f.q !== N && f.q !== 1 ? ` = ${f.p}/${f.q}` : '';
    const dec = exactWith(f, 3) ? ` = ${roundText(f, 3)}` : ` ≈ ${roundText(f, 2)}`;
    const pctText = exactWith(pct, 1)
      ? ` = ${roundText(pct, 1)}${NNBSP}%`
      : ` ≈ ${roundText(pct, 1)}${NNBSP}%`;
    const angle = mul(f, frac(360));
    return {
      freq: f,
      freqText: `${c.n}/${N}${reduced}${dec}${pctText}`,
      angle,
      angleText: exactWith(angle, 1) ? `${roundText(angle, 1)}°` : `≈ ${roundText(angle, 1)}°`,
    };
  });
}

/** pas « rond » de graduation pour un axe d'effectifs */
export function axisStep(max: number): number {
  for (const s of [1, 2, 5, 10, 20, 50]) if (max / s <= 8) return s;
  return 100;
}
